import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import HtmlToReact, { Parser } from 'html-to-react';
import { sanitize } from 'dompurify';
import {
  Accordion,
  AccordionSet,
  Badge,
  Button,
  Col,
  ConfirmationModal,
  Icon,
  KeyValue,
  MultiColumnList,
  Pane,
  Row,
  Tooltip,
} from '@folio/stripes/components';
import { AppIcon, CalloutContext, IfPermission, useOkapiKy, useStripes } from '@folio/stripes/core';
import { Link } from 'react-router-dom';
import { ViewMetaData } from '@folio/stripes/smart-components';
import { FormattedMessage, FormattedDate, FormattedTime, useIntl, } from 'react-intl';
import { useContext } from 'react';
import { MdPause, MdPlayArrow } from 'react-icons/md';
import { getLocalizedValue } from '../../helpers/localizationUtils';
import useScheduledReportsMutations from '../../hooks/useScheduledReportsMutations';
import useLastExecution from '../../hooks/useLastExecution';
import LastRunBadge from './LastRunBadge';
import RunNowModal from './RunNowModal';
import ExecutionHistoryModal from './ExecutionHistoryModal';
import ScheduleEditModal from './ScheduleEditModal';

function ScheduledReportDetails({ schedule: initialSchedule, onClose, onDeleted, onRefresh, onDeleteSchedule }) {
  const intl = useIntl();
  const callout = useContext(CalloutContext);
  const okapiKy = useOkapiKy();
  const okapiKyRef = useRef(okapiKy);
  const stripes = useStripes();

  const hasAnyScheduleAction =
    stripes.hasPerm('ui-reports.scheduled-reports.edit') ||
    stripes.hasPerm('ui-reports.scheduled-reports.delete') ||
    stripes.hasPerm('ui-reports.report-executions.view');
  useEffect(() => {
    okapiKyRef.current = okapiKy;
  }, [okapiKy]);

  // نسخة محلية من schedule تتحدث بعد التعديل مباشرة
  const [schedule, setSchedule] = useState(initialSchedule);
  useEffect(() => { setSchedule(initialSchedule); }, [initialSchedule.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const { pauseSchedule, resumeSchedule, runNow, deleteSchedule } =
    useScheduledReportsMutations();
  const {
    stage,
    execution,
    elapsed,
    startPolling,
    stopPolling,
    fetchLastExecution,
  } = useLastExecution(schedule.id);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRunNowModal, setShowRunNowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { locale } = useIntl();
  const [reportNameData, setReportNameData] = useState(null);

  useEffect(() => {
    if (!schedule.reportId) return;
    okapiKyRef.current
      .get(`reports/${schedule.reportId}`)
      .json()
      .then((r) => setReportNameData(r?.name ?? null))
      .catch(() => setReportNameData(null));
  }, [schedule.reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePauseResume = async () => {
    setIsProcessing(true);
    try {
      if (schedule.isActive) {
        await pauseSchedule.mutateAsync(schedule.id);
        setSchedule(prev => ({ ...prev, isActive: false }));
        callout.sendCallout({
          message: intl.formatMessage({
            id: 'ui-reports.scheduledReports.callout.paused',
          }),
          type: 'success',
        });
      } else {
        await resumeSchedule.mutateAsync(schedule.id);
        setSchedule(prev => ({ ...prev, isActive: true }));
        callout.sendCallout({
          message: intl.formatMessage({
            id: 'ui-reports.scheduledReports.callout.resumed',
          }),
          type: 'success',
        });
      }
    } catch (e) {
      callout.sendCallout({
        message: intl.formatMessage(
          { id: 'ui-reports.scheduledReports.callout.pauseResumeError' },
          { error: e.message },
        ),
        type: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRunNow = async () => {
    setIsProcessing(true);
    try {
      await runNow.mutateAsync(schedule.id);
      startPolling();
      setShowRunNowModal(true);
      // Refresh schedule data after run-now to update executionCount etc.
      if (onRefresh) setTimeout(onRefresh, 3000);
    } catch (e) {
      callout.sendCallout({
        message: intl.formatMessage(
          { id: 'ui-reports.scheduledReports.callout.runNowError' },
          { error: e.message },
        ),
        type: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    setIsProcessing(true);
    try {
      // Use stripesConnect DELETE if available (auto-refreshes list), else TanStack Query
      if (onDeleteSchedule) {
        await onDeleteSchedule(schedule.id);
      } else {
        await deleteSchedule.mutateAsync(schedule.id);
      }
      stopPolling();
      callout.sendCallout({
        message: intl.formatMessage({
          id: 'ui-reports.scheduledReports.callout.deleted',
        }),
        type: 'success',
      });
      setShowDeleteConfirm(false);
      if (onDeleted) onDeleted();
      onClose();
    } catch (e) {
      callout.sendCallout({
        message: intl.formatMessage(
          { id: 'ui-reports.scheduledReports.callout.deleteError' },
          { error: e.message },
        ),
        type: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const renderLastMenu = () => {
    if (!hasAnyScheduleAction) return null;
    return (
    <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
      <IfPermission perm="ui-reports.scheduled-reports.edit">
        <Tooltip
          id={`srd-edit-${schedule.id}`}
          text={<FormattedMessage id="ui-reports.scheduledReports.edit" defaultMessage="Edit" />}
        >
          {({ ref, ariaIds }) => (
            <Button ref={ref} aria-labelledby={ariaIds.text}
              buttonStyle="default" marginBottom0
              onClick={() => setShowEditModal(true)} disabled={isProcessing}
            >
              <Icon icon="edit" />
            </Button>
          )}
        </Tooltip>
      </IfPermission>

      <IfPermission perm="ui-reports.scheduled-reports.edit">
        <Tooltip
          id={`srd-pause-${schedule.id}`}
          text={
            schedule.isActive
              ? <FormattedMessage id="ui-reports.scheduledReports.pause" defaultMessage="Pause" />
              : <FormattedMessage id="ui-reports.scheduledReports.resume" defaultMessage="Resume" />
          }
        >
          {({ ref, ariaIds }) => (
            <Button ref={ref} aria-labelledby={ariaIds.text}
              buttonStyle="default" marginBottom0
              onClick={handlePauseResume} disabled={isProcessing}
            >
              {schedule.isActive ? <MdPause size={18} /> : <MdPlayArrow size={18} />}
            </Button>
          )}
        </Tooltip>
      </IfPermission>

      <IfPermission perm="ui-reports.scheduled-reports.edit">
        <Tooltip
          id={`srd-run-${schedule.id}`}
          text={<FormattedMessage id="ui-reports.scheduledReports.runNow" defaultMessage="Run Now" />}
        >
          {({ ref, ariaIds }) => (
            <Button ref={ref} aria-labelledby={ariaIds.text}
              buttonStyle="primary" marginBottom0
              onClick={handleRunNow} disabled={isProcessing || !schedule.isActive}
            >
              <Icon icon="lightning" />
            </Button>
          )}
        </Tooltip>
      </IfPermission>

      <IfPermission perm="ui-reports.scheduled-reports.delete">
        <Tooltip
          id={`srd-delete-${schedule.id}`}
          text={<FormattedMessage id="ui-reports.scheduledReports.delete" defaultMessage="Delete" />}
        >
          {({ ref, ariaIds }) => (
            <Button ref={ref} aria-labelledby={ariaIds.text}
              buttonStyle="danger" marginBottom0
              onClick={() => setShowDeleteConfirm(true)} disabled={isProcessing}
            >
              <Icon icon="trash" />
            </Button>
          )}
        </Tooltip>
      </IfPermission>
    </div>
    );
  };

  const recipientCount = schedule.recipients?.length || 0;

  // Parse HTML safely — same pattern as PatronNoticeEmailSection in ui-circulation
  const parseHtml = (html) => {
    if (!html) return null;
    const processNodeDefinitions = new HtmlToReact.ProcessNodeDefinitions(React);
    const parser = new Parser();
    const rules = [{ shouldProcessNode: () => true, processNode: processNodeDefinitions.processDefaultNode }];
    return parser.parseWithInstructions(sanitize(html), () => true, rules);
  };

  const formatDuration = (ms) => {
    if (!ms) return '—';
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  return (
    <>
      <Pane
        id="scheduled-report-details-pane"
        appIcon={<AppIcon app="reports" />}
        defaultWidth="40%"
        paneTitle={getLocalizedValue(schedule.name, locale) || ''}
        paneSub={
          <LastRunBadge
            stage={stage}
            execution={execution}
            elapsed={elapsed}
            onClick={() => setShowHistoryModal(true)}
          />
        }
        dismissible
        onClose={onClose}
        lastMenu={renderLastMenu()}
      >
        {schedule.metadata && <ViewMetaData metadata={schedule.metadata} />}
        <AccordionSet>
          {/* ── Basic Info ── */}
          <Accordion
            id="srd-basic-info"
            label={
              <FormattedMessage
                id="ui-reports.scheduledReports.basicInfo"
                defaultMessage="Basic Information"
              />
            }
          >
            <Row>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.name"
                      defaultMessage="Name"
                    />
                  }
                  value={getLocalizedValue(schedule.name, locale) || '—'}
                />
              </Col>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.status"
                      defaultMessage="Status"
                    />
                  }
                  value={
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '0.2em 0.6em',
                        borderRadius: '0.25rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        background: schedule.isActive ? '#d1e7dd' : '#e2e3e5',
                        color: schedule.isActive ? '#0a3622' : '#41464b',
                        border: schedule.isActive
                          ? '1px solid #a3cfbb'
                          : '1px solid #c4c8cb',
                      }}
                    >
                      {schedule.isActive ? (
                        <FormattedMessage id="ui-reports.scheduledReports.active" />
                      ) : (
                        <FormattedMessage id="ui-reports.scheduledReports.paused" />
                      )}
                    </span>
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.description"
                      defaultMessage="Description"
                    />
                  }
                  value={getLocalizedValue(schedule.description, locale) || '—'}
                />
              </Col>
            </Row>
          </Accordion>

          {/* ── Schedule ── */}
          <Accordion
            id="srd-schedule"
            label={
              <FormattedMessage
                id="ui-reports.scheduledReports.schedule"
                defaultMessage="Schedule"
              />
            }
          >
            <Row>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.frequency"
                      defaultMessage="Frequency"
                    />
                  }
                  value={
                    <FormattedMessage
                      id={`ui-reports.scheduledReports.frequency.${schedule.frequency}`}
                      defaultMessage={schedule.frequency || '—'}
                    />
                  }
                />
              </Col>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.cronExpression"
                      defaultMessage="Cron Expression"
                    />
                  }
                  value={<code>{schedule.cronExpression || '—'}</code>}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.startDate"
                      defaultMessage="Start Date"
                    />
                  }
                  value={
                    schedule.startDate ? (
                      <>
                        <FormattedDate value={schedule.startDate} />{' '}
                        <FormattedTime value={schedule.startDate} />
                      </>
                    ) : (
                      '—'
                    )
                  }
                />
              </Col>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.endDate"
                      defaultMessage="End Date"
                    />
                  }
                  value={
                    schedule.endDate ? (
                      <>
                        <FormattedDate value={schedule.endDate} />{' '}
                        <FormattedTime value={schedule.endDate} />
                      </>
                    ) : (
                      '—'
                    )
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.nextExecution"
                      defaultMessage="Next Execution"
                    />
                  }
                  value={
                    schedule.nextExecutionDate ? (
                      <>
                        <FormattedDate value={schedule.nextExecutionDate} />{' '}
                        <FormattedTime value={schedule.nextExecutionDate} />
                      </>
                    ) : (
                      '—'
                    )
                  }
                />
              </Col>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.executionCount"
                      defaultMessage="Total Executions"
                    />
                  }
                  value={schedule.executionCount ?? 0}
                />
              </Col>
            </Row>
          </Accordion>

          {/* ── Output ── */}
          <Accordion
            id="srd-output"
            label={
              <FormattedMessage
                id="ui-reports.scheduledReports.output"
                defaultMessage="Output Settings"
              />
            }
          >
            <Row>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.linkedReport"
                      defaultMessage="Linked Report"
                    />
                  }
                  value={
                    schedule.reportId ? (
                      <Link to={`/reports/reports-list/${schedule.reportId}/view`}>
                        {getLocalizedValue(reportNameData, locale) || schedule.reportId}
                      </Link>
                    ) : (
                      '—'
                    )
                  }
                />
              </Col>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.outputFormat"
                      defaultMessage="Format"
                    />
                  }
                  value={
                    <span
                      style={{ textTransform: 'uppercase', fontWeight: 'bold' }}
                    >
                      {schedule.outputFormat || '—'}
                    </span>
                  }
                />
              </Col>
            </Row>
          </Accordion>

          {/* ── Email ── */}
          <Accordion
            id="srd-email"
            label={
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FormattedMessage
                  id="ui-reports.scheduledReports.email"
                  defaultMessage="Email Settings"
                />
                {recipientCount > 0 && <Badge>{recipientCount}</Badge>}
              </span>
            }
          >
            <Row>
              <Col xs={12}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.emailSubject"
                      defaultMessage="Email Subject"
                    />
                  }
                  value={getLocalizedValue(schedule.emailSubject, locale) || '—'}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.emailBody"
                      defaultMessage="Email Body"
                    />
                  }
                  value={
                    getLocalizedValue(schedule.emailBody, locale)
                      ? parseHtml(getLocalizedValue(schedule.emailBody, locale))
                      : '—'
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.recipients"
                      defaultMessage="Recipients"
                    />
                  }
                  value={
                    recipientCount > 0 ? (
                      <MultiColumnList
                        id="srd-recipients-list"
                        interactive={false}
                        contentData={schedule.recipients}
                        visibleColumns={['name', 'email', 'locale']}
                        columnMapping={{
                          name:   <FormattedMessage id="ui-reports.recipients.name"   defaultMessage="Name" />,
                          email:  <FormattedMessage id="ui-reports.recipients.email"  defaultMessage="Email" />,
                          locale: <FormattedMessage id="ui-reports.recipients.locale" defaultMessage="Language" />,
                        }}
                        columnWidths={{ name: '35%', email: '45%', locale: '20%' }}
                        formatter={{
                          name:   (r) => r.name  || '—',
                          email:  (r) => r.email || '—',
                          locale: (r) => r.locale || '—',
                        }}
                      />
                    ) : (
                      '—'
                    )
                  }
                />
              </Col>
            </Row>
          </Accordion>

          {/* ── Last Execution ── */}
          <Accordion
            id="srd-last-execution"
            label={
              <FormattedMessage
                id="ui-reports.scheduledReports.lastExecution"
                defaultMessage="Last Execution"
              />
            }
          >
            <Row>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.lastExecutionDate"
                      defaultMessage="Last Run"
                    />
                  }
                  value={
                    schedule.lastExecutionDate ? (
                      <>
                        <FormattedDate value={schedule.lastExecutionDate} />{' '}
                        <FormattedTime value={schedule.lastExecutionDate} />
                      </>
                    ) : (
                      '—'
                    )
                  }
                />
              </Col>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.lastRunStatus"
                      defaultMessage="Last Status"
                    />
                  }
                  value={
                    <LastRunBadge
                      stage={stage}
                      execution={execution}
                      elapsed={elapsed}
                      onClick={() => setShowHistoryModal(true)}
                    />
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.lastRecordsCount"
                      defaultMessage="Records Exported"
                    />
                  }
                  value={
                    execution?.recordsCount != null
                      ? execution.recordsCount.toLocaleString()
                      : '—'
                  }
                />
              </Col>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.lastDuration"
                      defaultMessage="Duration"
                    />
                  }
                  value={formatDuration(execution?.duration)}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.lastEmailSent"
                      defaultMessage="Email Sent"
                    />
                  }
                  value={
                    execution?.emailSent === true ? (
                      <span
                        style={{
                          color: 'var(--color-success, #198754)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        <Icon icon="check-circle" size="small" />
                        <FormattedMessage
                          id="ui-reports.executionHistory.emailSent.yes"
                          defaultMessage="Sent"
                        />
                      </span>
                    ) : execution?.emailSent === false ? (
                      <span
                        style={{
                          color: 'var(--color-error, #dc3545)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        <Icon icon="times-circle" size="small" />
                        <FormattedMessage
                          id="ui-reports.executionHistory.emailSent.no"
                          defaultMessage="Not sent"
                        />
                      </span>
                    ) : (
                      '—'
                    )
                  }
                />
              </Col>
              <Col xs={6}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-reports.scheduledReports.lastErrorMessage"
                      defaultMessage="Error"
                    />
                  }
                  value={
                    execution?.errorMessage ? (
                      <span
                        style={{
                          color: 'var(--color-error, #dc3545)',
                          fontSize: '0.85rem',
                          wordBreak: 'break-word',
                        }}
                      >
                        {execution.errorMessage}
                      </span>
                    ) : (
                      '—'
                    )
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <Button
                  buttonStyle="default"
                  onClick={() => setShowHistoryModal(true)}
                >
                  <Icon icon="clock">
                    <FormattedMessage
                      id="ui-reports.scheduledReports.history"
                      defaultMessage="Execution History"
                    />
                  </Icon>
                </Button>
              </Col>
            </Row>
          </Accordion>
        </AccordionSet>
      </Pane>

      {/* ── Modals ── */}
      {showRunNowModal && (
        <RunNowModal
          open={showRunNowModal}
          onClose={() => setShowRunNowModal(false)}
          onViewHistory={() => {
            setShowRunNowModal(false);
            setShowHistoryModal(true);
          }}
          schedule={schedule}
          stage={stage}
          execution={execution}
          elapsed={elapsed}
        />
      )}

      {showHistoryModal && (
        <ExecutionHistoryModal
          open={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          schedule={schedule}
        />
      )}

      <ConfirmationModal
        open={showDeleteConfirm}
        heading={
          <FormattedMessage id="ui-reports.scheduledReports.delete.confirm.heading" />
        }
        message={
          <FormattedMessage id="ui-reports.scheduledReports.delete.confirm.message" />
        }
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmLabel={
          <FormattedMessage id="ui-reports.scheduledReports.delete.confirm.btn" />
        }
      />

      {showEditModal && (
        <ScheduleEditModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          schedule={schedule}
          onSuccess={(updatedSchedule) => {
            setShowEditModal(false);
            if (updatedSchedule) setSchedule(updatedSchedule);
          }}
        />
      )}
    </>
  );
}

ScheduledReportDetails.propTypes = {
  schedule: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onDeleted:        PropTypes.func,
  onRefresh:        PropTypes.func,
  onDeleteSchedule: PropTypes.func,
};

export default ScheduledReportDetails;
